/**
 * AI 报告页 — 包装原有 Briefings 功能，整合为统一的报告中心
 */
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { FilePenLine, Presentation } from 'lucide-react';

import { showToast } from '@/utils/toast';
import { PageShell } from '../components/PageShell';
import { usePriceIntelStore } from '../stores/price-intel-store';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-background-primary)',
  borderColor: 'var(--color-system-quaternary)',
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: 'var(--radius-xl)',
  boxShadow: 'var(--shadow-sm)',
};

const mutedText: React.CSSProperties = { color: 'var(--color-system-secondary)' };

const inputStyle: React.CSSProperties = {
  borderColor: 'var(--color-system-quaternary)',
  backgroundColor: 'var(--color-background-primary)',
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: 'var(--radius-md)',
};

export function IntelReportsPage() {
  const { briefings, fetchBriefings, createBriefing } = usePriceIntelStore();
  const [title, setTitle] = useState('竞品价格周报');
  const [reportingPeriod, setReportingPeriod] = useState('');
  const [competitor, setCompetitor] = useState('');

  useEffect(() => {
    void fetchBriefings();
  }, [fetchBriefings]);

  const handleCreate = async () => {
    try {
      await createBriefing({
        title,
        reporting_period: reportingPeriod || undefined,
        competitor: competitor || undefined,
      });
      showToast('AI 报告已生成');
    } catch (error) {
      showToast(`生成失败: ${(error as Error).message}`);
    }
  };

  return (
    <PageShell>
      <div className="space-y-5">
        {/* 头部 */}
        <div>
          <h1
            className="flex items-center gap-2 text-xl font-semibold"
            style={{ color: 'var(--color-system-primary)' }}
          >
            <Presentation size={20} style={{ color: 'var(--color-key)' }} />
            AI 报告
          </h1>
          <p className="mt-1 text-sm" style={mutedText}>
            基于已录入数据，AI 辅助生成市场分析报告和领导汇报。
          </p>
        </div>

        <div className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
          {/* 生成表单 */}
          <div className="p-5" style={cardStyle}>
            <div className="flex items-center gap-3">
              <div
                className="rounded-lg p-2.5"
                style={{ backgroundColor: 'rgba(0, 113, 227, 0.1)', color: 'var(--color-key)' }}
              >
                <FilePenLine size={17} />
              </div>
              <h2 className="text-lg font-semibold">生成 AI 报告</h2>
            </div>
            <div className="mt-4 space-y-2.5">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2.5 text-sm outline-none focus:ring-2"
                style={inputStyle}
                placeholder="报告标题"
              />
              <input
                value={reportingPeriod}
                onChange={(e) => setReportingPeriod(e.target.value)}
                className="w-full px-3 py-2.5 text-sm outline-none focus:ring-2"
                style={inputStyle}
                placeholder="报告周期（如：2026Q1）"
              />
              <input
                value={competitor}
                onChange={(e) => setCompetitor(e.target.value)}
                className="w-full px-3 py-2.5 text-sm outline-none focus:ring-2"
                style={inputStyle}
                placeholder="指定竞品（可选）"
              />
              <button
                onClick={() => void handleCreate()}
                className="rounded-lg px-4 py-2 text-sm font-medium text-white transition hover:opacity-90"
                style={{ backgroundColor: 'var(--color-key)' }}
              >
                生成报告
              </button>
            </div>
          </div>

          {/* 报告列表 */}
          <div className="space-y-3">
            {briefings.length === 0 ? (
              <div
                className="rounded-xl border border-dashed px-4 py-10 text-center text-sm"
                style={{ borderColor: 'var(--color-system-quaternary)', ...mutedText }}
              >
                暂无报告。点击「生成 AI 报告」创建第一份。
              </div>
            ) : (
              briefings.map((briefing) => (
                <article key={briefing.id} className="p-5" style={cardStyle}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-base font-semibold">{briefing.title}</h3>
                      <p className="mt-1 text-sm" style={mutedText}>
                        {dayjs(briefing.created_at).format('YYYY-MM-DD HH:mm')} · {briefing.reporting_period || 'latest'}
                      </p>
                    </div>
                    <div
                      className="rounded-full px-2.5 py-1 text-xs font-medium"
                      style={{ backgroundColor: 'var(--color-background-secondary)', color: 'var(--color-system-secondary)' }}
                    >
                      <span className="inline-flex items-center">
                        <Presentation size={12} className="mr-1" />
                        AI Report
                      </span>
                    </div>
                  </div>
                  <pre
                    className="mt-3 whitespace-pre-wrap rounded-lg p-3 text-sm leading-6"
                    style={{ backgroundColor: 'var(--color-background-secondary)', color: 'var(--color-system-primary)' }}
                  >
                    {briefing.summary_markdown}
                  </pre>
                </article>
              ))
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
